import React, { useState } from "react";
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Switch,
  Row,
  Col,
  message,
  Alert,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { Component, PRICING_EVENTS } from "../../types/pricing";

import { trackEvent, captureError } from "../../lib/posthog";

const { Option } = Select;

interface ComponentsTabProps {
  components: Component[];
  loading: boolean;
  fetchComponents: () => Promise<void>;
}

interface ComponentFormValues {
  name: string;
  description?: string;
  type: string;
  calculation_type: string;
  is_active?: boolean;
  applies_to_delivery_type?: "delivery" | "storage" | "both" | null;
  applies_to_shipment_type?: "carton" | "pallet" | "both" | null;
  applies_to_booking_type?: "fcl" | "lcl" | "both" | null;
  applies_to_unloading_location?: "at_location" | "at_efreight" | "both" | null;
}

const ComponentsTab: React.FC<ComponentsTabProps> = ({
  components,
  loading,
  fetchComponents,
}) => {
  const [componentForm] = Form.useForm<ComponentFormValues>();
  const [editingComponent, setEditingComponent] = useState<Component | null>(
    null
  );

  const handleComponentSubmit = async (values: ComponentFormValues) => {
    try {
      // Map form values to database structure
      const componentData: {
        name: string;
        description?: string;
        component_type: string;
        calculation_type: string;
        applies_to_delivery_type?: "delivery" | "storage" | "both" | null;
        applies_to_shipment_type?: "carton" | "pallet" | "both" | null;
        applies_to_booking_type?: "fcl" | "lcl" | "both" | null;
        applies_to_unloading_location?: "at_location" | "at_efreight" | "both" | null;
      } = {
        name: values.name,
        description: values.description,
        component_type: values.type,
        calculation_type: values.calculation_type || "fixed", // default to fixed
      };

      // Handle applies_to_delivery_type
      // When user explicitly selects "both", save as "both"
      // When user clears selection or it's undefined/null, keep null for backward compatibility
      if (values.applies_to_delivery_type === "both") {
        componentData.applies_to_delivery_type = "both";
      } else if (values.applies_to_delivery_type === "delivery" || values.applies_to_delivery_type === "storage") {
        componentData.applies_to_delivery_type = values.applies_to_delivery_type;
      } else {
        // null or undefined - keep as null for backward compatibility with existing components
        componentData.applies_to_delivery_type = null;
      }

      // Handle applies_to_shipment_type
      // When user explicitly selects "both", save as "both"
      // When user clears selection or it's undefined/null, keep null for backward compatibility
      if (values.applies_to_shipment_type === "both") {
        componentData.applies_to_shipment_type = "both";
      } else if (values.applies_to_shipment_type === "carton" || values.applies_to_shipment_type === "pallet") {
        componentData.applies_to_shipment_type = values.applies_to_shipment_type;
      } else {
        // null or undefined - keep as null for backward compatibility with existing components
        componentData.applies_to_shipment_type = null;
      }

      // Handle applies_to_booking_type
      if (values.applies_to_booking_type === "both") {
        componentData.applies_to_booking_type = "both";
      } else if (values.applies_to_booking_type === "fcl" || values.applies_to_booking_type === "lcl") {
        componentData.applies_to_booking_type = values.applies_to_booking_type;
      } else {
        componentData.applies_to_booking_type = "both";
      }

      // Handle applies_to_unloading_location
      if (values.applies_to_unloading_location === "both") {
        componentData.applies_to_unloading_location = "both";
      } else if (values.applies_to_unloading_location === "at_location" || values.applies_to_unloading_location === "at_efreight") {
        componentData.applies_to_unloading_location = values.applies_to_unloading_location;
      } else {
        componentData.applies_to_unloading_location = "both";
      }

      if (editingComponent) {
        // Update existing component
        const { error } = await supabase
          .from("pricing_components")
          .update(componentData)
          .eq("id", editingComponent.id);

        if (error) throw error;
        message.success("Component updated successfully");

        // Track component update
        trackEvent(PRICING_EVENTS.UPDATE_COMPONENT, {
          component_id: editingComponent.id,
          component_name: values.name,
          component_type: values.type,
        });
      } else {
        // Insert new component
        const { data, error } = await supabase
          .from("pricing_components")
          .insert(componentData)
          .select();

        if (error) throw error;
        message.success("Component added successfully");

        // Track component creation
        trackEvent(PRICING_EVENTS.ADD_COMPONENT, {
          component_id: data?.[0]?.id,
          component_name: values.name,
          component_type: values.type,
        });
      }

      // Refresh the components list
      fetchComponents();
      setEditingComponent(null);
      componentForm.resetFields();
    } catch (error: unknown) {
      console.error("Error saving component:", error);
      message.error("Failed to save component");
      captureError(error, {
        source: "ComponentsTab",
        action: editingComponent ? "updateComponent" : "createComponent",
        componentData: values,
      });
    }
  };

  const handleEditComponent = (record: Component) => {
    console.log("[DEBUG] Editing component:", record);
    setEditingComponent(record);
    // When editing, convert null to "both" for the form display (null means backward compatibility)
    const deliveryType = (record as any).applies_to_delivery_type;
    const formDeliveryType = deliveryType === null || deliveryType === undefined ? "both" : deliveryType;

    const shipmentType = (record as any).applies_to_shipment_type;
    const formShipmentType = shipmentType === null || shipmentType === undefined ? "both" : shipmentType;

    const bookingType = (record as any).applies_to_booking_type;
    const formBookingType = bookingType === null || bookingType === undefined ? "both" : bookingType;

    const unloadingLocation = (record as any).applies_to_unloading_location;
    const formUnloadingLocation = unloadingLocation === null || unloadingLocation === undefined ? "both" : unloadingLocation;

    componentForm.setFieldsValue({
      name: record.name,
      description: record.description,
      type: record.component_type || record.type || "",
      calculation_type: record.calculation_type || "",
      // is_active is not a database column, so we don't set it from the record
      is_active: true, // Default value for form display
      applies_to_delivery_type: formDeliveryType,
      applies_to_shipment_type: formShipmentType,
      applies_to_booking_type: formBookingType,
      applies_to_unloading_location: formUnloadingLocation,
    });
  };

  const handleDeleteComponent = async (id: string) => {
    try {
      const { error } = await supabase
        .from("pricing_components")
        .delete()
        .eq("id", id);

      if (error) throw error;

      message.success("Component deleted successfully");

      // Track component deletion
      trackEvent(PRICING_EVENTS.DELETE_COMPONENT, {
        component_id: id,
      });

      fetchComponents();
    } catch (error: unknown) {
      console.error("Error deleting component:", error);
      message.error("Failed to delete component");
      captureError(error, {
        source: "ComponentsTab",
        action: "deleteComponent",
        componentId: id,
      });
    }
  };

  const componentColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Calculation",
      dataIndex: "calculation_type",
      key: "calculation_type",
    },

    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (active: boolean) => <span>{active ? "Yes" : "No"}</span>,
    },
    {
      title: "Applies To",
      dataIndex: "applies_to_delivery_type",
      key: "applies_to_delivery_type",
      width: 120,
      render: (value: string | null, record: Component) => {
        const deliveryType = (record as any).applies_to_delivery_type;
        if (!deliveryType || deliveryType === null || deliveryType === "both") {
          return <Tag color="default">📦🚚 Both</Tag>;
        }
        if (deliveryType === "delivery") {
          return <Tag color="blue">🚚 Delivery</Tag>;
        }
        if (deliveryType === "storage") {
          return <Tag color="orange">📦 Storage</Tag>;
        }
        return <Tag color="default">📦🚚 Both</Tag>;
      },
    },
    {
      title: "Shipment Type",
      dataIndex: "applies_to_shipment_type",
      key: "applies_to_shipment_type",
      width: 130,
      render: (value: string | null, record: Component) => {
        const shipmentType = (record as any).applies_to_shipment_type;
        if (!shipmentType || shipmentType === null || shipmentType === "both") {
          return <Tag color="default">🎁🛠 Both</Tag>;
        }
        if (shipmentType === "carton") {
          return <Tag color="green">🎁 Carton</Tag>;
        }
        if (shipmentType === "pallet") {
          return <Tag color="purple">🛠 Pallet</Tag>;
        }
        return <Tag color="default">🎁🛠 Both</Tag>;
      },
    },
    {
      title: "Booking Type",
      dataIndex: "applies_to_booking_type",
      key: "applies_to_booking_type",
      width: 100,
      render: (_: unknown, record: Component) => {
        const bookingType = (record as any).applies_to_booking_type;
        if (!bookingType || bookingType === "both") {
          return <Tag color="default">Both</Tag>;
        }
        if (bookingType === "fcl") {
          return <Tag color="blue">FCL</Tag>;
        }
        if (bookingType === "lcl") {
          return <Tag color="cyan">LCL</Tag>;
        }
        return <Tag color="default">Both</Tag>;
      },
    },
    {
      title: "Unloading",
      dataIndex: "applies_to_unloading_location",
      key: "applies_to_unloading_location",
      width: 110,
      render: (_: unknown, record: Component) => {
        const unloading = (record as any).applies_to_unloading_location;
        if (!unloading || unloading === "both") {
          return <Tag color="default">Both</Tag>;
        }
        if (unloading === "at_location") {
          return <Tag color="green">Op locatie</Tag>;
        }
        if (unloading === "at_efreight") {
          return <Tag color="blue">Bij E-Freight</Tag>;
        }
        return <Tag color="default">Both</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: Component) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditComponent(record)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteComponent(record.id.toString())}
          />
        </Space>
      ),
    },
  ];

  return (
    <Row gutter={24}>
      <Col xs={24} lg={8}>
        <Card
          title={editingComponent ? "Edit Component" : "Add New Component"}
          extra={
            <Alert
              type="info"
              showIcon
              message="True Inheritance System"
              description="All components are automatically inherited by child nodes in the pricing tree. No behavior settings needed!"
              style={{ marginBottom: 0, fontSize: "12px" }}
              banner
            />
          }
        >
          <Form
            form={componentForm}
            layout="vertical"
            onFinish={handleComponentSubmit}
          >
            <Form.Item
              name="name"
              label="Component Name"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="type"
              label="Component Type"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select>
                <Option value="base">Base</Option>
                <Option value="fee">Fee</Option>
                <Option value="surcharge">Surcharge</Option>
                <Option value="service">Service</Option>
                <Option value="insurance">Insurance</Option>
                <Option value="discount">Discount</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="calculation_type"
              label="Calculation Type"
              rules={[
                {
                  required: true,
                  message: "Please select a calculation type",
                },
              ]}
            >
              <Select>
                <Option value="fixed">Fixed Amount</Option>
                <Option value="percentage">Percentage</Option>
                <Option value="per_cbm">Per CBM</Option>
                <Option value="per_weight">Per KG</Option>
                <Option value="per_product">Per Product</Option>
                <Option value="per_unique_product">
                  Per Unique Product (HS Code)
                </Option>
                <Option value="per_carton">Per Carton</Option>
                <Option value="per_pallet">Per Pallet</Option>
                <Option value="per_shipment">Per Shipment</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="applies_to_delivery_type"
              label="Applies To Delivery Type"
              tooltip="Select when this component should be included: Both (default) = always included, Delivery = only for delivery shipments, Storage = only for storage shipments"
            >
              <Select allowClear placeholder="Both (default)">
                <Option value="both">Both - Applies to delivery and storage</Option>
                <Option value="delivery">Delivery Only</Option>
                <Option value="storage">Storage Only</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="applies_to_shipment_type"
              label="Applies To Shipment Type"
              tooltip="Select which shipment types this component applies to: Both (default) = always included, Carton = only for carton shipments, Pallet = only for pallet shipments"
            >
              <Select allowClear placeholder="Both (default)">
                <Option value="both">Both - Applies to carton and pallet shipments</Option>
                <Option value="carton">Carton Only - &lt; 8 cartons</Option>
                <Option value="pallet">Pallet Only - ≥ 8 cartons or forced pallet</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="applies_to_booking_type"
              label="Applies To Booking Type"
              tooltip="FCL = Seafreight full container only. LCL = Seafreight less than container. Airfreight = separate (no FCL/LCL). Both = Seafreight FCL + Seafreight LCL + Airfreight"
            >
              <Select allowClear placeholder="Both (default)">
                <Option value="both">Both - Seafreight FCL, Seafreight LCL, and Airfreight</Option>
                <Option value="fcl">FCL Only - Seafreight full container</Option>
                <Option value="lcl">LCL Only - Seafreight less than container + Airfreight</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="applies_to_unloading_location"
              label="Applies To Unloading Location"
              tooltip="Derived from delivery type: at_location = FCL+Delivery (direct to customer, geen lossingskosten), at_efreight = FCL+Storage/LCL/Airfreight (wel lossingskosten), both = all"
            >
              <Select allowClear placeholder="Both (default)">
                <Option value="both">Both - All unloading locations</Option>
                <Option value="at_location">Op locatie - Lossen op locatie</Option>
                <Option value="at_efreight">Bij E-Freight - Lossen bij E-Freight</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingComponent ? "Update" : "Add"} Component
                </Button>
                {editingComponent && (
                  <Button
                    onClick={() => {
                      setEditingComponent(null);
                      componentForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={16}>
        <Card title="Pricing Components">
          <Table
            columns={componentColumns}
            dataSource={components}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ComponentsTab;
